/*!
 * SunburstNav — mehrstufige, animierte Ring-/Piechart-Navigation
 * Vanilla JS, keine Abhängigkeiten. SVG-basiert, themebar über CSS Custom Properties.
 *
 * Verwendung:
 *   import createSunburstNav from './sunburst-nav.js';
 *   const nav = createSunburstNav('#chart', { data: myData });
 *
 * Siehe README.md für die vollständige Dokumentation des Datenobjekts.
 */

import { DEMO_SUNBURST_DATA } from './mod_sunburst-data.js'

const VIEW = 1000;           // interne SVG-Koordinaten (viewBox 0 0 1000 1000)
const C = VIEW / 2;          // Mittelpunkt
const DEG = Math.PI / 180;

/* ------------------------------------------------------------------ *
 * Defaults
 * ------------------------------------------------------------------ */

const DEFAULTS = {
  data: null,

  // Geometrie
  startAngle: -90,           // -90 = 12 Uhr
  outerRadius: 0.94,         // Anteil von 500
  coreRadius: 0.34,          // Anteil von 500 (Kreis in der Mitte, aufgeklappt)
  coreRadiusRoot: 0.28,      // Kern auf Ebene 0 — bewusst kleiner für Decor-Gap
  coreGap: 51,               // Luft zwischen Kern und innerem Ring (innere Ticks)
  ringGap: 6,                // Abstand zwischen den Ringen (SVG-Einheiten)
  ringWidths: null,          // z.B. [130, 80, 60] – überschreibt die Gleichverteilung
  padAngle: 0.6,             // Lücke zwischen Segmenten in Grad
  spanOuterPad: 28,          // Außen-Inset für Ebene-1-Flächen (Root + inaktive Keile)
  maxRings: 4,               // harte Obergrenze sichtbarer Ringe
  focusBoost: 1,             // >1: der geöffnete Zweig bekommt mehr Winkel (Akkordeon)
  // Rem-Labels wachsen in User-Units wenn das SVG schrumpft — Ringe können
  // dagegen leicht aufdicken (relativ zu ringScaleRef, gedeckelt mit ringScaleMax).
  ringScaleRef: 1000,
  ringScaleMax: 2,

  // Interaktion / Motion
  hover:  { offset: 12, scale: 1.025 },
  detach: { offset: 27, scale: 1.06, rotate: 0, rotateMinAngle: 14 }, // ausgeworfene Karte beim Blattklick
  motion: { move: 450, enter: 520, exit: 380, stagger: 45, drift: 90 }, // ms, drift in SVG-Einheiten
  decor: false,              // rotierende Skalen-/Tick-Ringe
  animate: true,             // global; prefers-reduced-motion überschreibt
  collapseOnRepeat: true,    // erneuter Klick auf offenes Segment schließt es

  // Inhalte
  center: {
    eyebrow: '', title: '',
    // Aus dem Entwurf gemessen, bezogen auf den Radius der sichtbaren Scheibe:
    eyebrowRatio: 0.212,   // Versalhöhe
    titleRatio:   0.322,   // Zifferhöhe
    eyebrowBase: -0.110,   // Grundlinie
    titleBase:    0.331,
    capRatio:     0.72     // Versalhöhe / Schriftgrad der verwendeten Schrift
  },
  showHeadline: true,
  showBackButton: true,      // HTML-Button unter dem Chart (die Zurück-Bögen gibt es zusätzlich)
  backLabel: 'icon',         // 'icon' = nur Pfeil, 'full' = Pfeil + Titel (nur bei breiten Bögen)
  labels: {
    maxChars: 16, maxLines: 3, minAngle: 0.6,
    mode: 'auto',          // 'auto' | 'radial' | 'horizontal'
    minRingWidth: 44,      // schmalere Ringe zeigen nur noch den Zurück-Pfeil
    size: 21,              // 2. Ebene Fallback (User-Units); CSS-Token hat Vorrang
    branchSize: 42,        // oberste Ebene ("Beraten")
    bandSize: 13,          // Kontextband
    maxSizePx: 21,         // harte Obergrenze 2. Ebene in CSS-Pixeln (Deckel, kein Zielwert)
    branchMaxChars: 18,    // kürzere Zeilen → mehr Luft zum Rand
    branchMaxLines: 4,     // Nummer + bis zu 4 Namenszeilen
    branchPad: 32,         // Label-Inset Ebene 1 (SVG-Einheiten, radial)
    branchPadAngle: 3,     // Label-Inset Ebene 1 (Grad, tangential)
    horizontalRatio: 2,    // ab wie viel Platzüberschuss quer statt radial gesetzt wird
    flip: 'upright'        // 'upright' = nie mehr als 90° gekippt | 'outward' = immer nach außen lesend
  },
  gradients: true,         // Verlaufsfüllung für Segmente und Kern

  // Verhalten
  hashRouting: false,
  hashKey: 'nav',
  compactBreakpoint: 1480,   // Viewport-Breite in px, darunter Rad + Panel gestapelt werden

  // Texte
  i18n: { back: 'Zurück', root: 'Übersicht', open: 'öffnen', openAll: 'Alle Bereiche', level: 'Ebene' },

  // Custom Renderer für die Infobox: (node) => HTMLElement | string | null
  renderInfo: null
};

/* ------------------------------------------------------------------ *
 * Helfer
 * ------------------------------------------------------------------ */

const SVGNS = 'http://www.w3.org/2000/svg';
const el = (name, attrs) => {
  const n = document.createElementNS(SVGNS, name);
  if (attrs) for (const k in attrs) n.setAttribute(k, attrs[k]);
  return n;
};

function isPlainObject(v) { return v && typeof v === 'object' && !Array.isArray(v); }
function merge(base, override) {
  const out = Array.isArray(base) ? base.slice() : Object.assign({}, base);
  for (const k in override) {
    const v = override[k];
    out[k] = isPlainObject(v) && isPlainObject(base[k]) ? merge(base[k], v) : v;
  }
  return out;
}

const polar = (r, deg) => [C + r * Math.cos(deg * DEG), C + r * Math.sin(deg * DEG)];

function donutPath(rIn, rOut) {
  return `M${C - rOut} ${C} A${rOut} ${rOut} 0 1 1 ${C + rOut} ${C} A${rOut} ${rOut} 0 1 1 ${C - rOut} ${C} Z` +
         `M${C - rIn} ${C} A${rIn} ${rIn} 0 1 0 ${C + rIn} ${C} A${rIn} ${rIn} 0 1 0 ${C - rIn} ${C} Z`;
}

/* Nur die nach außen zeigende Kante – für den orangen Hover-Rand. */
function outerEdgePath(r, a0, a1) {
  const span = a1 - a0;
  if (Math.abs(span) >= 359.99) {
    return `M${C - r} ${C} A${r} ${r} 0 1 1 ${C + r} ${C} A${r} ${r} 0 1 1 ${C - r} ${C}`;
  }
  const sweep = span >= 0 ? 1 : 0;
  const large = Math.abs(span) > 180 ? 1 : 0;
  const [x0, y0] = polar(r, a0);
  const [x1, y1] = polar(r, a1);
  return `M${x0} ${y0} A${r} ${r} 0 ${large} ${sweep} ${x1} ${y1}`;
}

function arcPath(rIn, rOut, a0, a1) {
  const span = a1 - a0;
  if (Math.abs(span) >= 359.99) return donutPath(rIn, rOut);
  const sweep = span >= 0 ? 1 : 0;
  const large = Math.abs(span) > 180 ? 1 : 0;
  const [x0, y0] = polar(rOut, a0);
  const [x1, y1] = polar(rOut, a1);
  const [x2, y2] = polar(rIn, a1);
  const [x3, y3] = polar(rIn, a0);
  return `M${x0} ${y0} A${rOut} ${rOut} 0 ${large} ${sweep} ${x1} ${y1} ` +
         `L${x2} ${y2} A${rIn} ${rIn} 0 ${large} ${1 - sweep} ${x3} ${y3} Z`;
}

function wrapLabel(text, maxChars, maxLines, truncate = true) {
  const words = String(text).split(/\s+/).filter(Boolean);
  const lines = [];
  let cur = '';
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + ' ' + w).length <= maxChars) cur += ' ' + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (truncate && lines.length > maxLines) {
    const rest = lines.slice(maxLines - 1).join(' ');
    lines.length = maxLines - 1;
    lines.push(rest.length > maxChars ? rest.slice(0, maxChars - 1) + '…' : rest);
  }
  return lines;
}

/* Hauptsegmente: lieber vor „und“ umbrechen, bis zu maxLines Zeilen, kein Truncate. */
function wrapBranchLabel(text, maxChars, maxLines) {
  const raw = String(text).trim();
  if (!raw) return [];
  // „… Planen und Zertifizieren“ → „… Planen“ / „und Zertifizieren“
  const chunks = raw.split(/(\s+und\s+)/i).filter(Boolean);
  const parts = [];
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    if (/^\s*und\s*$/i.test(chunk)) {
      const next = chunks[i + 1] || '';
      parts.push(`und ${next}`.trim());
      i++;
    } else if (!/^\s*und\s+/i.test(chunk)) {
      parts.push(chunk.trim());
    }
  }
  const lines = [];
  for (const part of (parts.length ? parts : [raw])) {
    lines.push(...wrapLabel(part, maxChars, maxLines, false));
  }
  return lines.slice(0, maxLines);
}

/* ------------------------------------------------------------------ *
 * Factory
 * ------------------------------------------------------------------ */

export function createSunburstNav(host, userOptions = {}) {
  const root = typeof host === 'string' ? document.querySelector(host) : host;
  if (!root) throw new Error('[SunburstNav] Host-Element nicht gefunden.');
  if (!userOptions.data) throw new Error('[SunburstNav] options.data fehlt.');

  const opt = merge(DEFAULTS, userOptions);

  /* ---------- Datenbaum in interne Knoten übersetzen ---------- */
  const registry = new Map();
  let autoId = 0;

  function build(raw, parent, depth, index) {
    const node = {
      id: raw.id != null ? String(raw.id) : `n${++autoId}`,
      raw,
      label: raw.label != null ? String(raw.label) : '',
      weight: typeof raw.weight === 'number' && raw.weight > 0 ? raw.weight : 1,
      color: raw.color || null,
      info: raw.info || null,
      href: raw.href || null,
      parent, depth, index,
      children: []
    };
    if (registry.has(node.id)) console.warn(`[SunburstNav] Doppelte id "${node.id}".`);
    registry.set(node.id, node);
    (raw.children || []).forEach((c, i) => node.children.push(build(c, node, depth + 1, i)));
    return node;
  }

  const tree = build(opt.data, null, 0, 0);
  const depthOf = n => (n.children.length ? 1 + Math.max(...n.children.map(depthOf)) : 0);
  const dataDepth = depthOf(tree);
  const ringCount = Math.max(1, Math.min(dataDepth, opt.maxRings));
  if (dataDepth > opt.maxRings) {
    console.warn(`[SunburstNav] Daten haben ${dataDepth} Ebenen, es werden max. ${opt.maxRings} Ringe gezeichnet.`);
  }

  /* ---------- Ringgeometrie ---------- */
  const rOuter = C * opt.outerRadius;
  let rings = [];
  let rCore = 60;
  let lastRingBoost = 0;
  let viewDrilled = false; // true sobald eine Ebene aufgeklappt ist

  function baseRingWidths() {
    if (Array.isArray(opt.ringWidths) && opt.ringWidths.length >= ringCount) {
      return opt.ringWidths.slice(0, ringCount);
    }
    const avail = rOuter - C * opt.coreRadius - (ringCount - 1) * opt.ringGap;
    return new Array(ringCount).fill(avail / ringCount);
  }

  function ringBoostFromSize() {
    const ref = opt.ringScaleRef || 900;
    const max = opt.ringScaleMax || 1.8;
    const w = svg && svg.clientWidth;
    if (!w || w >= ref) return 1;
    return Math.min(max, ref / w);
  }

  function rebuildRings(boost = 1) {
    const gap = opt.coreGap || 51;
    const minCore = viewDrilled ? 48 : 72;
    const coreRatio = viewDrilled
      ? opt.coreRadius
      : (opt.coreRadiusRoot || Math.max(opt.coreRadius, 0.28));

    let widths;
    if (!viewDrilled) {
      // Root: ein Ring + Luft zum Kern (innere Decor-Ticks)
      const targetCore = C * coreRatio;
      widths = [Math.max(80, rOuter - targetCore - gap)];
    } else {
      const base = baseRingWidths();
      // Äußerer Ring (Labels) wächst stärker, Kontextband nur leicht
      widths = base.map((w, i) => (
        i === 0 ? w * boost : w * (1 + (boost - 1) * 0.25)
      ));
    }

    const maxUse = rOuter - minCore - gap - (widths.length - 1) * opt.ringGap;
    let sum = widths.reduce((a, b) => a + b, 0);
    if (sum > maxUse) {
      let overflow = sum - maxUse;
      for (let i = widths.length - 1; i >= 1 && overflow > 0.5; i--) {
        const baseW = (baseRingWidths()[i] || widths[i]);
        const floor = baseW * 0.55;
        const cut = Math.min(overflow, widths[i] - floor);
        if (cut > 0) {
          widths[i] -= cut;
          overflow -= cut;
        }
      }
      if (overflow > 0.5) {
        widths[0] = Math.max(80, widths[0] - overflow);
      }
    }

    const next = [];
    let o = rOuter;
    for (let i = 0; i < widths.length; i++) {
      next.push({ outer: o, inner: o - widths[i] });
      o = o - widths[i] - opt.ringGap;
    }
    rings = next;
    // Kern bleibt hinter dem Gap — sonst verschwinden die inneren Ticks
    rCore = Math.max(minCore, Math.min(C * coreRatio, rings[rings.length - 1].inner - gap));
  }

  function applyRingGeometry(force = false) {
    const boost = ringBoostFromSize();
    if (!force && Math.abs(boost - lastRingBoost) < 0.02) {
      applyCoreFontSizes();
      liveSegs.forEach((g, id) => {
        const node = registry.get(String(id));
        if (!node || !g._label) return;
        g._labelKey = '';
        paintLabel(g, node, g.classList.contains('is-open'), Number(g.getAttribute('data-level')));
      });
      return;
    }
    lastRingBoost = boost;
    rebuildRings(boost);
    paintDecor();
    paintCore();
    liveSegs.forEach((g, id) => {
      const node = registry.get(String(id));
      if (!node) return;
      const ringIdx = Number(g.getAttribute('data-level'));
      if (!rings[ringIdx]) return;
      paintGeometry(g, geomOf({
        node,
        ringIdx,
        dim: g.classList.contains('is-dim'),
        open: g.classList.contains('is-open')
      }));
      g._labelKey = '';
      paintLabel(g, node, g.classList.contains('is-open'), ringIdx);
    });
  }

  rebuildRings(1);

  /* ---------- DOM ---------- */
  root.classList.add('sb');
  root.innerHTML = '';
  root.style.setProperty('--sb-dur-move', `${opt.motion.move}ms`);
  root.style.setProperty('--sb-dur-enter', `${opt.motion.enter}ms`);
  root.style.setProperty('--sb-dur-exit', `${opt.motion.exit}ms`);

  // Häufigste Ursache für "die Animationen laufen nicht": das Betriebssystem
  // meldet reduzierte Bewegung. Einmal melden, damit man nicht im Code sucht.
  const prefersReduced = typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (prefersReduced) {
    console.info('[SunburstNav] prefers-reduced-motion ist aktiv – alle Animationen sind abgeschaltet. ' +
                 'Systemeinstellung prüfen (macOS: Bedienungshilfen › Anzeige › Bewegung reduzieren).');
  }

  const headline = document.createElement('h2');
  headline.className = 'sb__headline';
  if (!opt.showHeadline) headline.style.display = 'none';

  const panel = document.createElement('div');
  panel.className = 'sb__panel';

  const stage = document.createElement('div');
  stage.className = 'sb__stage';

  const svg = el('svg', {
    class: 'sb__svg',
    viewBox: `0 0 ${VIEW} ${VIEW}`,
    role: 'application',
    'aria-label': opt.data.label || opt.i18n.root
  });

  // Verläufe brauchen eindeutige IDs, sonst kollidieren mehrere Instanzen
  // auf derselben Seite.
  const uid = `sb${Math.random().toString(36).slice(2, 8)}`;
	if (opt.gradients) {
		// Kern: radialer Bevel (Highlight oben links → Schatten am Rand).
		// objectBoundingBox, damit der Verlauf am Disc sitzt, nicht am ganzen Rad.
		const defs = el('defs');
		const coreGrad = el('radialGradient', {
			id: `${uid}-core`,
			gradientUnits: 'objectBoundingBox',
			cx: '0.34', cy: '0.26', r: '0.88'
		});
		coreGrad.append(
			el('stop', { offset: '0%', 'stop-color': 'var(--sb-core-highlight, #edebe9)' }),
			el('stop', { offset: '48%', 'stop-color': 'var(--sb-core-mid, #cbc8c5)' }),
			el('stop', { offset: '100%', 'stop-color': 'var(--sb-core-shadow, #a4a19f)' })
		);
		defs.appendChild(coreGrad);
		svg.appendChild(defs);
		root.style.setProperty('--sb-core-fill', `url(#${uid}-core)`);
	}

  const gDecor  = el('g', { class: 'sb-decor', 'aria-hidden': 'true' });
  const gGuides = el('g', { class: 'sb-guides', 'aria-hidden': 'true' });
  const gLevels = el('g', { class: 'sb-levels' });
  const gCore   = el('g', { class: 'sb-core' });
  svg.append(gDecor, gGuides, gLevels, gCore);

  const backWrap = document.createElement('div');
  backWrap.className = 'sb__backwrap';
  const backBtn = document.createElement('button');
  backBtn.type = 'button';
  backBtn.className = 'sb__back';
  backBtn.innerHTML = `<span aria-hidden="true">←</span> ${opt.i18n.back}`;
  backWrap.appendChild(backBtn);

  if (opt.showBackButton) stage.append(svg, backWrap);
  else stage.append(svg);

  const live = document.createElement('p');
  live.className = 'sb__sr';
  live.setAttribute('aria-live', 'polite');

  const mirror = document.createElement('div');   // reiner SEO-/Crawler-Spiegel
  mirror.className = 'sb__sr';
  mirror.setAttribute('aria-hidden', 'true');
  mirror.innerHTML = renderMirror(tree);

  root.append(headline, panel, stage, live, mirror);

  function renderMirror(node) {
    if (!node.children.length) return '';
    return '<ul>' + node.children.map(c =>
      `<li>${c.href ? `<a href="${c.href}">${escapeHtml(c.label)}</a>` : escapeHtml(c.label)}${renderMirror(c)}</li>`
    ).join('') + '</ul>';
  }
  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, m => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[m]));
  }

  /* ---------- Dekor + Kern ---------- */
  function paintDecor() {
    gDecor.innerHTML = '';
    gGuides.innerHTML = '';

    rings.forEach(r => {
      gGuides.appendChild(el('circle', { class: 'sb-guide', cx: C, cy: C, r: r.outer }));
      gGuides.appendChild(el('circle', { class: 'sb-guide', cx: C, cy: C, r: r.inner }));
    });

    if (!opt.decor) return;

    gDecor.appendChild(el('circle', { class: 'sb-decor__dashed', cx: C, cy: C, r: rOuter + 26 }));
    gDecor.appendChild(el('circle', { class: 'sb-decor__dotted', cx: C, cy: C, r: rOuter + 14 }));

    const outerTicks = el('g', { class: 'sb-ticks' });
    for (let i = 0; i < 36; i++) {
      const a = i * 10;
      const major = i % 3 === 0;
      const len = major ? 20 : 11;
      const [x1, y1] = polar(rOuter + 6, a);
      const [x2, y2] = polar(rOuter + 6 + len, a);
      const t = el('line', { class: 'sb-tick' + (major ? '' : ' sb-tick--mute'), x1, y1, x2, y2 });
      t.style.animationDelay = `${(i % 9) * 0.34}s`;
      outerTicks.appendChild(t);
    }
    gDecor.appendChild(outerTicks);

    const innerTicks = el('g', { class: 'sb-ticks sb-ticks--inner' });
    const hole = rings.length ? rings[rings.length - 1].inner : rCore + 40;
    const gapSpace = Math.max(0, hole - rCore);
    if (gapSpace >= 16) {
      const tickBase = rCore + Math.min(12, gapSpace * 0.3);
      const maxLen = Math.max(4, Math.min(10, gapSpace - (tickBase - rCore) - 4));
      for (let i = 0; i < 16; i++) {
        const a = i * 22.5 + 6;
        const len = i % 2 ? maxLen * 0.55 : maxLen;
        const [x1, y1] = polar(tickBase, a);
        const [x2, y2] = polar(tickBase + len, a);
        const t = el('line', { class: 'sb-tick', x1, y1, x2, y2 });
        t.style.animationDelay = `${(i % 7) * 0.45}s`;
        innerTicks.appendChild(t);
      }
      gDecor.appendChild(innerTicks);
    }
  }

  function paintCore() {
    gCore.innerHTML = '';
    const atRoot = focusPath.length === 1 && !selected && !overviewAll;
    const disc = el('circle', {
      class: 'sb-core__disc',
      cx: C, cy: C, r: rCore,
      role: 'button',
      tabindex: '0',
      'aria-label': atRoot ? (opt.i18n.openAll || 'Alle Bereiche') : opt.i18n.root
    });
    disc.addEventListener('click', e => { e.preventDefault(); e.stopPropagation(); onCoreActivate(); });
    disc.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
        e.preventDefault();
        onCoreActivate();
      }
    });
    gCore.appendChild(disc);

    const rDisc = rCore;
    const { eyebrow, title } = opt.center;
    if (eyebrow) {
      const t = el('text', {
        class: 'sb-core__eyebrow', x: C, y: (C + opt.center.eyebrowBase * rDisc).toFixed(1),
        'aria-hidden': 'true'
      });
      t.textContent = eyebrow;
      gCore.appendChild(t);
    }
    if (title) {
      const t = el('text', {
        class: 'sb-core__title', x: C,
        // ohne Eyebrow: Titel mittig im Kern
        y: (C + (eyebrow ? opt.center.titleBase : 0.08) * rDisc).toFixed(1),
        'aria-hidden': 'true'
      });
      t.textContent = title;
      gCore.appendChild(t);
    }
    applyCoreFontSizes();
  }

  /* ---------- State ---------- */
  let focusPath = [tree];   // [root, ...geöffnete Knoten]
  let selected = null;      // ausgewähltes Blatt
  let overviewAll = false;  // Root-Kern: alle Level-2 gleichzeitig
  let pendingFocusId = null;
  let hoverId = null;       // Segment unter dem Zeiger, überlebt den Umbau
  const liveSegs = new Map(); // Signatur -> gerendertes Segment (ohne Abgänge)
  const timers = new Set(); // laufende Aufräum-Timeouts
  const listeners = {};

  const on = (evt, fn) => { (listeners[evt] || (listeners[evt] = [])).push(fn); return api; };
  const off = (evt, fn) => { if (listeners[evt]) listeners[evt] = listeners[evt].filter(f => f !== fn); return api; };
  const emit = (evt, payload) => (listeners[evt] || []).forEach(f => f(payload));

  /* ---------- Layout & Render ---------- */

  // Alle Segmente liegen flach in gLevels. Ein Segment, das den Ring wechselt,
  // wird dadurch nie umgehängt – das würde laufende Transitions verwerfen.
  // Die Ringzugehörigkeit steht in data-level.

  function layout(parent, a0, a1) {
    // Der geöffnete Zweig kann mehr Winkel bekommen, damit tiefe Ebenen
    // nicht auf unlesbare Splitter zusammenschrumpfen.
    const next = focusPath[focusPath.indexOf(parent) + 1] || null;
    const boost = c => c.weight * (c === next && opt.focusBoost > 1 ? opt.focusBoost : 1);
    const total = parent.children.reduce((s, c) => s + boost(c), 0) || 1;
    let a = a0;
    parent.children.forEach(c => {
      const span = (a1 - a0) * (boost(c) / total);
      c.a0 = a; c.a1 = a + span; a += span;
    });
  }

  /* ---------- Geometrie: Bewegung statt Neuaufbau ---------- */

  function geomOf(d) {
    const ring = rings[d.ringIdx];
    if (!ring) return { rIn: 0, rOut: 0, a0: d.node.a0, a1: d.node.a1 };
    let rIn = ring.inner;
    let rOut = ring.outer;
    const inset = opt.spanOuterPad || 28;

    // Inaktive Geschwister: Keil bis zum äußeren Ring — mit Außen-Padding
    if (rings.length > 1 && d.ringIdx > 0 && d.dim && !d.open) {
      rOut = Math.max(rIn + 40, rings[0].outer - inset);
    }

    // Ebene 1 (auch Root): Fläche nicht bis zum Ringrand — Luft fürs Label
    if (d.node.depth === 1 && !d.open && (rOut - rIn) > inset * 2 + 40) {
      rOut -= inset;
      rIn += Math.min(14, inset * 0.4);
    }

    return { rIn, rOut, a0: d.node.a0, a1: d.node.a1 };
  }

  /* Zeichnet ein Segment für einen beliebigen Zwischenzustand neu. Pfaddaten
     lassen sich nicht per CSS interpolieren – deshalb rechnen wir sie pro
     Frame selbst. Bei ~15 Segmenten ist das vernachlässigbar. */
  function paintGeometry(g, m) {
    const span = m.a1 - m.a0;
    // Aufgespannte Geschwister: etwas mehr Winkel-Padding → Text nicht am Rand
    const basePad = opt.padAngle + (g.classList.contains('is-dim') ? 1.4 : 0);
    const pad = Math.abs(span) > basePad * 3 ? basePad : 0;
    const a0 = m.a0 + pad / 2, a1 = m.a1 - pad / 2;
    g._shape.setAttribute('d', arcPath(m.rIn, m.rOut, a0, a1));
    g._edge.setAttribute('d', outerEdgePath(m.rOut, a0, a1));

    const mid = (m.a0 + m.a1) / 2;
    const rMid = (m.rIn + m.rOut) / 2;
    const [cx, cy] = polar(rMid, mid);
    g._tilt.style.transformOrigin = `${cx}px ${cy}px`;
    g._vec = { ux: Math.cos(mid * DEG), uy: Math.sin(mid * DEG) };
    g._span = Math.abs(span);

    g._geom = m;
    placeLabel(g);
    // Wandert ein Segment den Ring, ändern sich Radius und Winkelhalbierende.
    // Hover- und Auswurf-Verschiebung müssen mitlaufen, sonst driftet die
    // Karte während der Bewegung von ihrem Segment weg.
    if (g._state === 'hover' || g._state === 'selected') applyTransform(g, g._state);
  }

  /* Jedes tspan braucht ein eigenes x. Ohne das setzt der Browser die
     Folgezeile dort fort, wo die vorige endete – die Zeilen treppen dann
     nach rechts unten weg. */
  function placeLabel(g) {
    if (!g._label || !g._geom) return;
    const m = g._geom;
    const depth = Number(g.getAttribute('data-depth'));
    let rIn = m.rIn;
    let rOut = m.rOut;
    let a0 = m.a0;
    let a1 = m.a1;

    // Ebene-1-Label: zusätzliches Innen-Padding (Text nicht am Flächenrand)
    if (depth === 1) {
      const padR = opt.labels.branchPad || 32;
      const padA = opt.labels.branchPadAngle || 3;
      const h = rOut - rIn;
      if (h > padR * 1.5) {
        rIn += padR * 0.35;
        rOut -= padR * 0.65;
      }
      if (Math.abs(a1 - a0) > padA * 4) {
        a0 += padA;
        a1 -= padA;
      }
    }

    const mid = (a0 + a1) / 2;
    const rMid = (rIn + rOut) / 2;
    const [cx, cy] = polar(rMid, mid);
    g._label.setAttribute('x', cx);
    g._label.setAttribute('y', cy - ((g._lines - 1) * g._lh) / 2);
    for (const ts of g._label.children) ts.setAttribute('x', cx);

    // Radial setzen, wenn der Bogen zu schmal für waagerechte Schrift ist.
    // Die Zeilen stapeln sich dann quer zur Leserichtung, weil die Rotation
    // das gesamte Textelement samt seiner y-Versätze mitdreht.
    let radial = opt.labels.mode === 'radial';
    if (opt.labels.mode === 'auto') {
      const arcLen = Math.abs(a1 - a0) * DEG * rMid;
      radial = arcLen < g._textWidth * opt.labels.horizontalRatio;
    }
    if (radial) {
      let rot = ((mid % 360) + 360) % 360;
      // 'upright': Schrift steht nie mehr als 90° gekippt – auf der linken
      // Kreishälfte liest sie dadurch nach innen statt nach außen.
      if (opt.labels.flip === 'upright' && rot > 90 && rot < 270) rot -= 180;
      g._label.setAttribute('transform', `rotate(${rot.toFixed(2)} ${cx.toFixed(2)} ${cy.toFixed(2)})`);
    } else {
      g._label.removeAttribute('transform');
    }
  }

  const easeInOut = t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  function tweenGeometry(g, to, dur) {
    if (g._tween) { cancelAnimationFrame(g._tween); g._tween = null; }
    const from = g._geom;
    const unchanged = Math.abs(from.rIn - to.rIn) < 0.01 && Math.abs(from.rOut - to.rOut) < 0.01 &&
                      Math.abs(from.a0 - to.a0) < 0.01 && Math.abs(from.a1 - to.a1) < 0.01;
    if (unchanged) return;
    if (dur <= 0 || typeof requestAnimationFrame !== 'function') { paintGeometry(g, to); return; }

    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const step = now => {
      const p = Math.min(1, ((now || Date.now()) - t0) / dur);
      const e = easeInOut(p);
      paintGeometry(g, {
        rIn:  from.rIn  + (to.rIn  - from.rIn)  * e,
        rOut: from.rOut + (to.rOut - from.rOut) * e,
        a0:   from.a0   + (to.a0   - from.a0)   * e,
        a1:   from.a1   + (to.a1   - from.a1)   * e
      });
      if (p < 1) g._tween = requestAnimationFrame(step);
      else { g._tween = null; paintGeometry(g, to); }
    };
    g._tween = requestAnimationFrame(step);
  }

  /* Bestehendes Segment auf einen neuen Zustand umstellen. */
  function retarget(g, d) {
    const isOpen = !!d.open;
    g.classList.toggle('is-open', isOpen);
    g.classList.toggle('is-dim', !isOpen && !!d.dim);
    g.setAttribute('data-level', String(d.ringIdx));
    g.setAttribute('data-depth', String(d.node.depth));
    g.setAttribute('aria-label', isOpen
      ? `${opt.i18n.back}: ${d.node.label}`
      : `${d.node.label}${d.node.children.length ? `, ${d.node.children.length} ${opt.i18n.open}` : ''}`);
    if (g._title) g._title.textContent = isOpen ? `${opt.i18n.back}: ${d.node.label}` : d.node.label;
    paintLabel(g, d.node, isOpen, d.ringIdx);
    tweenGeometry(g, geomOf(d), opt.animate ? opt.motion.move : 0);
  }

  /* Richtung des Wechsels bestimmt, wohin ein- und ausgeblendet wird. */
  function pathMode(from, to) {
    const isPrefix = (a, b) => a.length <= b.length && a.every((n, i) => n === b[i]);
    if (isPrefix(from, to)) return 'deeper';
    if (isPrefix(to, from)) return 'higher';
    return 'replace';
  }

  function describe() {
    // Übersicht aller Level-2: beide Hauptzweige im Band, alle Kinder außen
    if (overviewAll && focusPath.length === 1) {
      const out = [];
      tree.children.forEach(branch => {
        out.push({ node: branch, ringIdx: 1, dim: false, open: true });
        branch.children.forEach(node => {
          out.push({ node, ringIdx: 0, dim: false, open: false });
        });
      });
      return out;
    }

    const len = focusPath.length;
    const out = [];
    for (let ringIdx = 0; ringIdx < len && ringIdx < rings.length; ringIdx++) {
      const parent = focusPath[len - 1 - ringIdx];
      if (!parent.children.length) continue;
      const activeChild = ringIdx === 0 ? null : focusPath[len - ringIdx];
      parent.children.forEach(node => out.push({
        node, ringIdx,
        dim: ringIdx > 0 && node !== activeChild,
        open: node === activeChild
      }));
    }
    return out;
  }

  function render(mode = 'deeper') {
    headline.textContent = focusPath.length > 1 ? focusPath[focusPath.length - 1].label : '';
    headline.classList.toggle('is-visible', focusPath.length > 1);
    backBtn.classList.toggle('is-visible', focusPath.length > 1 || !!selected || overviewAll);
    root.classList.toggle('is-overview-all', overviewAll);

    // Root: ein dicker Ring + größerer Kern. Aufgeklappt / Übersicht: zwei Ringe.
    const drilled = focusPath.length > 1 || overviewAll;
    root.classList.toggle('is-drilled', drilled);
    if (drilled !== viewDrilled) {
      viewDrilled = drilled;
      lastRingBoost = ringBoostFromSize();
      rebuildRings(lastRingBoost);
      paintDecor();
      paintCore();
    } else {
      const disc = gCore.querySelector('.sb-core__disc');
      if (disc) {
        const atRoot = focusPath.length === 1 && !selected && !overviewAll;
        disc.setAttribute('aria-label', atRoot ? (opt.i18n.openAll || 'Alle Bereiche') : opt.i18n.root);
      }
    }

    // Jede Ebene wird im Winkelbereich ihres Elternteils aufgeteilt. Ring 0
    // deckt damit exakt denselben Kreisausschnitt ab wie der geöffnete Knoten
    // im Ring darunter – die Ringe bleiben radial übereinander.
    tree.a0 = opt.startAngle;
    tree.a1 = opt.startAngle + 360;
    if (overviewAll && focusPath.length === 1) {
      layout(tree, tree.a0, tree.a1);
      tree.children.forEach(n => { if (n.children.length) layout(n, n.a0, n.a1); });
    } else {
      focusPath.forEach(n => { if (n.children.length) layout(n, n.a0, n.a1); });
    }

    // Außen ist die Bühne – aber nur für echte Zu- und Abgänge. Alles, was in
    // beiden Zuständen vorkommt, wandert auf direktem Weg an seinen neuen Platz.
    const d = opt.animate ? opt.motion.drift : 0;
    const enterFrom = mode === 'higher' ? -d : d;
    const exitTo    = mode === 'deeper' ? -d : d;
    const enterDelay = mode === 'replace' ? opt.motion.exit : 0;

    // Ein Knoten kommt pro Zustand in höchstens einem Ring vor – die ID
    // genügt daher als Identität über Zustandswechsel hinweg.
    const next = new Map();
    describe().forEach(item => next.set(item.node.id, item));

    // --- Abgänge: nur Knoten, die es im neuen Zustand gar nicht mehr gibt ---
    [...liveSegs.entries()].forEach(([id, g]) => {
      if (next.has(id)) return;
      liveSegs.delete(id);
      exitSegment(g, exitTo);
    });

    // --- Bestand bewegen, Zugänge vormerken ---
    const fresh = [];
    next.forEach((item, id) => {
      const existing = liveSegs.get(id);
      if (existing) { retarget(existing, item); return; }
      fresh.push({ item, g: buildSegment(item.node, item.ringIdx, item) });
    });

    // --- Zugänge: im Startzustand einhängen, einen Frame später lösen ---
    // Ring 0 hinter dem Kontextband einfügen — sonst liegen frische Outer-Segmente
    // während der Animation kurz über dem aktiven Level-1 (Primary-Fill).
    fresh.forEach(({ item, g }, i) => {
      liveSegs.set(item.node.id, g);
      g.style.opacity = '0';
      g.style.setProperty('--sb-delay', `${enterDelay + i * opt.motion.stagger}ms`);
      applyOffset(g, enterFrom, 1);
      if (item.ringIdx === 0) {
        const band = gLevels.querySelector('.sb-seg[data-level="1"]');
        if (band) gLevels.insertBefore(g, band);
        else gLevels.appendChild(g);
      } else {
        gLevels.appendChild(g);
      }
    });

    const selNode = selected;
    if (fresh.length) {
      raf2(() => fresh.forEach(({ item, g }) => {
        g.style.opacity = '1';
        // Auswahl hat Vorrang: sonst zieht ein noch laufender Zugang das
        // bereits ausgeworfene Segment wieder in die Ruhelage zurück.
        if (g.classList.contains('is-selected')) return;
        applyTransform(g, hoverId === item.node.id ? 'hover' : 'idle');
      }));
      // Verzögerung wieder abräumen, sonst hinkt später jeder Hover hinterher.
      const maxDelay = enterDelay + (fresh.length - 1) * opt.motion.stagger + opt.motion.enter;
      after(maxDelay + 60, () => fresh.forEach(({ g }) => g.style.setProperty('--sb-delay', '0ms')));
    }

    // --- Auswahl ---
    // Immer aus der Ruhelage nach außen fahren. Frische Segmente kommen von
    // weiter außen herein; würde man den Auswurf sofort setzen, rutschten sie
    // nach innen statt heraus. Deshalb erst ankommen lassen, dann auswerfen.
    const freshIds = new Set(fresh.map(f => f.item.node.id));
    liveSegs.forEach((g, id) => {
      const item = next.get(id);
      const isSel = !!selNode && item && item.node === selNode && item.ringIdx === 0;
      if (isSel === g.classList.contains('is-selected')) return;
      if (isSel) {
        if (!freshIds.has(id)) {
          // Bestehendes Segment: exakt derselbe Weg wie beim Abwählen –
          // Klasse setzen, Transform setzen, fertig. Kein Umhängen, keine
          // Verzögerung, damit die Transition sicher anläuft.
          g.classList.add('is-selected');
          applyTransform(g, 'selected');
          return;
        }
        if (g._selTimer) return;                // Auswurf ist schon eingeplant
        // Frisches Segment kommt noch von außen herein – erst ankommen lassen.
        g._selTimer = after(enterDelay + opt.motion.enter, () => {
          g._selTimer = null;
          if (selected !== item.node) return;   // inzwischen wieder abgewählt
          g.classList.add('is-selected');
          applyTransform(g, 'selected');
        });
      } else {
        if (g._selTimer) { clearTimeout(g._selTimer); timers.delete(g._selTimer); g._selTimer = null; }
        g.classList.remove('is-selected');
        applyTransform(g, hoverId === id ? 'hover' : 'idle');
      }
    });

    renderPanel();

    if (pendingFocusId) {
      const t = gLevels.querySelector(`.sb-seg:not(.is-exiting)[data-id="${CSS_escape(pendingFocusId)}"]`);
      if (t) t.focus({ preventScroll: true });
      pendingFocusId = null;
    }
  }

  /* Kein Umhängen im DOM – nirgends.
     Eine Verschiebung entlang der Winkelhalbierenden verkleinert den
     Winkelbereich jedes Punktes (der radiale Anteil wächst, der tangentiale
     bleibt). Ein ausgeworfenes Segment kann seinen eigenen Sektor deshalb
     gar nicht verlassen und braucht keine Sonderbehandlung in der
     Malreihenfolge. Nachgerechnet für 4,4° bis 180°: der äußerste Punktwinkel
     bleibt in jedem Fall unter dem Halbwinkel des Segments.

     Das ist wichtig, weil jedes Umhängen den Transitionszustand verwirft:
     der Browser behandelt das Element danach als frisch eingefügt und
     springt an den Zielwert, statt dorthin zu animieren. */

  function exitSegment(g, drift) {
    if (g._tween) { cancelAnimationFrame(g._tween); g._tween = null; }
    if (g._selTimer) { clearTimeout(g._selTimer); timers.delete(g._selTimer); g._selTimer = null; }
    g.classList.add('is-exiting');
    g.classList.remove('is-selected');
    g.setAttribute('tabindex', '-1');
    g.removeAttribute('data-sig');
    raf2(() => {
      g.style.setProperty('--sb-delay', '0ms');
      g.style.opacity = '0';
      applyOffset(g, drift, 1.02);
    });
    after(opt.motion.exit + 80, () => { if (g.parentNode) g.parentNode.removeChild(g); });
  }

  function after(ms, fn) {
    const id = setTimeout(() => { timers.delete(id); fn(); }, ms);
    timers.add(id);
    return id;
  }

  // Zwei Frames warten: Frame 1 legt den Ausgangszustand fest, Frame 2 startet
  // die Transition. Mit nur einem rAF fasst der Browser beides zusammen.
  function raf2(fn) {
    if (typeof requestAnimationFrame !== 'function') return fn();
    requestAnimationFrame(() => requestAnimationFrame(fn));
  }

  function CSS_escape(s) {
    return (window.CSS && window.CSS.escape) ? window.CSS.escape(s) : String(s).replace(/["\\]/g, '\\$&');
  }

  /* rem/em sind Bildschirmmaße; px/unitless bleiben SVG-User-Units (viewBox 1000).
     Ohne Umrechnung skaliert die viewBox rem auf wenige Pixel – wirkt wie „greift nicht“. */
  function cssPxToUser(cssPx) {
    const w = svg.clientWidth;
    return w > 0 ? cssPx * (VIEW / w) : cssPx;
  }

  function tokenToUser(raw, fallback) {
    const v = String(raw || '').trim();
    if (!v) return fallback;
    const n = parseFloat(v);
    if (!Number.isFinite(n)) return fallback;
    if (v.endsWith('rem')) {
      return cssPxToUser(n * parseFloat(getComputedStyle(document.documentElement).fontSize));
    }
    if (v.endsWith('em')) {
      return cssPxToUser(n * parseFloat(getComputedStyle(root).fontSize));
    }
    return n;
  }

  function readSizeToken(varName, fallback) {
    return tokenToUser(getComputedStyle(root).getPropertyValue(varName), fallback);
  }

  function coreSizeFallback(kind) {
    const cap = opt.center.capRatio || 0.72;
    const ratio = kind === 'eyebrow' ? opt.center.eyebrowRatio : opt.center.titleRatio;
    return ratio / cap * rCore;
  }

  /* SVG-Text: rem/em aus CSS-Tokens → User-Units (viewBox 1000). */
  function applyCoreFontSizes() {
    const eyebrow = gCore.querySelector('.sb-core__eyebrow');
    const title = gCore.querySelector('.sb-core__title');
    if (eyebrow) {
      eyebrow.style.fontSize = `${readSizeToken('--sb-core-eyebrow-size', coreSizeFallback('eyebrow'))}px`;
    }
    if (title) {
      title.style.fontSize = `${readSizeToken('--sb-core-title-size', coreSizeFallback('title'))}px`;
    }
  }

  function labelSizePx(node, ringIdx) {
    // Hauptsegmente (Ebene 1) behalten Branch-Größe auch im Kontextband
    if (node.depth === 1) return readSizeToken('--sb-label-branch-size', opt.labels.branchSize);
    if (ringIdx !== 0) return readSizeToken('--sb-label-band-size', opt.labels.bandSize);
    // 2. Ebene: Token lesen, aber nie über 21 CSS-Pixel hinaus
    const fs = readSizeToken('--sb-label-size', opt.labels.size);
    const cap = cssPxToUser(opt.labels.maxSizePx || 21);
    return Math.min(fs, cap);
  }

  function branchNumber(node) {
    // Gegen die Datenreihenfolge: visuell 01 = letzter Geschwisterknoten
    const total = (node.parent && node.parent.children.length) || 1;
    return String(total - node.index).padStart(2, '0');
  }

  /* Baut Label-Zeilen neu auf. Wird auch beim Umschalten auf/von "offen"
     gebraucht, weil dann der Zurück-Pfeil dazukommt oder verschwindet. */
  function paintLabel(g, node, isOpen, ringIdx) {
    if (!g._label) return;

    // Schmales Kontextband: der volle Titel passt nicht mehr in die Ringhöhe.
    // Dann bleibt nur der Zurück-Pfeil am geöffneten Knoten stehen; der
    // Klartext steckt weiterhin in aria-label und im nativen Tooltip.
    // Aufgespannte Geschwister nutzen die volle Keilhöhe (nicht nur das Band).
    const ring = rings[ringIdx];
    const rIn = ring ? ring.inner : 0;
    const rOut = (rings.length > 1 && ringIdx > 0 && !isOpen && g.classList.contains('is-dim') && rings[0])
      ? rings[0].outer
      : (ring ? ring.outer : 0);
    const schmal = (rOut - rIn) < opt.labels.minRingWidth;

    const isBranch = node.depth === 1;
    const num = isBranch ? branchNumber(node) : null;

    let lines;
    if (isBranch) {
      // Nur bei aktivem Segment den Namen ausblenden — inaktiv immer Nummer + Text
      if (isOpen) {
        lines = [num];
      } else {
        // Bis 4 Namenszeilen; Umbruch bevorzugt vor „und“
        const nameLines = wrapBranchLabel(
          node.label,
          opt.labels.branchMaxChars || 18,
          opt.labels.branchMaxLines || 4
        );
        lines = [num, ...nameLines];
      }
    } else if (schmal) {
      lines = isOpen && opt.backLabel !== 'none' ? ['←'] : [];
    } else {
      lines = wrapLabel(node.label, opt.labels.maxChars, opt.labels.maxLines);
      if (isOpen && opt.backLabel !== 'none') lines[0] = `← ${lines[0]}`;
    }

    // Dieselben Werte wie im CSS – sonst passt die Zeilenhöhe nicht zur Schrift
    // und mehrzeilige Labels überlappen. Aufgespannte Geschwister behalten Band-Größe.
    const fs = labelSizePx(node, ringIdx);
    g._label.style.fontSize = `${fs}px`;
    const lh = fs * 1.18;
    // Radial gestellte Schrift braucht Platz quer zum Radius: die Bogenlänge
    // auf Höhe des Labels muss den Zeilenblock fassen. Waagerechte Schrift
    // hängt dagegen am Öffnungswinkel.
    const span = Math.abs(node.a1 - node.a0);
    // Hauptsegmente: Nummer möglichst behalten — nur bei extrem engem Winkel ausblenden
    const zuEng = !lines.length || (isBranch
      ? span < opt.labels.minAngle * 0.35
      : ((opt.labels.mode === 'radial' && g._geom)
        ? span * DEG * ((g._geom.rIn + g._geom.rOut) / 2) < lines.length * lh
        : span < opt.labels.minAngle));
    g._label.classList.toggle('is-hidden', zuEng);

    // Unverändert? Dann nicht neu aufbauen – jeder Neuaufbau kostet die
    // x-Attribute der tspans und muss danach zwingend neu positioniert werden.
    const key = `${fs}|${lh}|${lines.join('\n')}|${num || ''}`;
    if (g._labelKey === key) return;
    g._labelKey = key;
    g._lh = lh;
    g._lines = lines.length;
    // Grobe Breitenschätzung – reicht, um radial gegen waagerecht abzuwägen.
    g._textWidth = (lines.length ? Math.max(...lines.map(l => l.length)) : 0) * fs * 0.55;

    g._label.textContent = '';
    lines.forEach((line, i) => {
      const attrs = { x: 0, dy: i === 0 ? 0 : lh };
      if (isBranch && i === 0) attrs.class = 'sb-seg__num';
      const ts = el('tspan', attrs);
      ts.textContent = line;
      g._label.appendChild(ts);
    });
    placeLabel(g);
  }

  function buildSegment(node, ringIdx, state) {
    const isOpen = !!(state && state.open);
    const g = el('g', {
      class: 'sb-seg',
      'data-id': node.id,
      'data-level': String(ringIdx),
      'data-depth': String(node.depth),
      'data-branch': node.children.length ? '1' : '0',
      tabindex: '0',
      role: 'button',
      'aria-label': isOpen
        ? `${opt.i18n.back}: ${node.label}`
        : `${node.label}${node.children.length ? `, ${node.children.length} ${opt.i18n.open}` : ''}`
    });
    if (isOpen) g.classList.add('is-open');
    else if (state && state.dim) g.classList.add('is-dim');
    if (node.color) g.style.setProperty('--sb-node-color', node.color);

    const title = el('title');
    title.textContent = isOpen ? `${opt.i18n.back}: ${node.label}` : node.label;
    g.appendChild(title);

    const gPush  = el('g', { class: 'sb-seg__push' });
    const gScale = el('g', { class: 'sb-seg__scale' });
    gScale.style.transformOrigin = `${C}px ${C}px`;
    const gTilt = el('g', { class: 'sb-seg__tilt' });

    const shape = el('path', { class: 'sb-seg__shape', 'fill-rule': 'evenodd' });
    const edge  = el('path', { class: 'sb-seg__edge' });
    gTilt.append(shape, edge);

    let label = null;
    if (node.label) {
      label = el('text', { class: 'sb-seg__label' });
      gTilt.appendChild(label);
    }

    gScale.appendChild(gTilt);
    gPush.appendChild(gScale);
    g.appendChild(gPush);

    g._push = gPush; g._scale = gScale; g._tilt = gTilt;
    g._shape = shape; g._edge = edge; g._label = label; g._title = title;
    g._tween = null;

    paintLabel(g, node, isOpen, ringIdx);
    paintGeometry(g, geomOf({
      node,
      ringIdx,
      dim: !!(state && state.dim),
      open: isOpen
    }));

    // Ausgangszustand VOR dem Einfügen setzen. Stand der Zeiger schon auf
    // diesem Knoten, startet das Segment direkt im Hover-Zustand – sonst
    // würde es beim Umbau auf Null zurückspringen.
    applyTransform(g, hoverId === node.id ? 'hover' : 'idle');

    g.addEventListener('pointerenter', () => {
      hoverId = node.id;
      if (selected !== node) applyTransform(g, 'hover');
    });
    g.addEventListener('pointerleave', () => {
      if (hoverId === node.id) hoverId = null;
      applyTransform(g, selected === node ? 'selected' : 'idle');
    });
    g.addEventListener('focus', () => { if (selected !== node) applyTransform(g, 'hover'); });
    g.addEventListener('blur',  () => applyTransform(g, selected === node ? 'selected' : 'idle'));
    g.addEventListener('click', e => { e.preventDefault(); activate(node); });
    g.addEventListener('keydown', e => onSegmentKey(e, () => activate(node)));

    return g;
  }

  /* Freie Verschiebung entlang der Winkelhalbierenden – für die Ein- und
     Ausblend-Positionen außerhalb bzw. innerhalb des Rings. */
  function applyOffset(g, dist, sc) {
    g._state = null;                    // Zu-/Abgang: nicht nachführen
    writeTransform(g, dist, sc || 1, 0);
  }

  /* Die Skalierung um den Kreismittelpunkt verschiebt ein Segment bereits
     radial nach außen – und zwar umso stärker, je weiter außen es liegt.
     Dieser Anteil wird abgezogen, damit `offset` genau die sichtbare Distanz
     ist. Ohne das flögen die äußeren Ringe deutlich weiter als gewollt. */
  function writeTransform(g, off, sc, rot) {
    const { ux, uy } = g._vec;
    const rMid = g._geom ? (g._geom.rIn + g._geom.rOut) / 2 : 0;
    const push = off - rMid * (sc - 1);
    g._push.style.transform  = `translate(${(ux * push).toFixed(3)}px, ${(uy * push).toFixed(3)}px)`;
    g._scale.style.transform = `scale(${sc})`;
    g._tilt.style.transform  = `rotate(${rot}deg)`;
  }

  /* Immer explizite Werte schreiben (auch die Identität) – "none" lässt sich
     nicht zuverlässig in beide Richtungen interpolieren. */
  function applyTransform(g, state) {
    g._state = state;
    let off = 0, sc = 1, rot = 0;
    if (state === 'hover')    { off = opt.hover.offset;  sc = opt.hover.scale; }
    if (state === 'selected') {
      off = opt.detach.offset; sc = opt.detach.scale;
      // Standardmäßig 0: eine Rotation um den Schwerpunkt kippt den Bogen aus
      // der Kreisform – die eine Innenecke wandert nach außen, die andere nach
      // innen. Verschiebung entlang der Winkelhalbierenden plus Skalierung um
      // den Kreismittelpunkt halten das Segment dagegen exakt konzentrisch.
      rot = g._span >= opt.detach.rotateMinAngle ? opt.detach.rotate : 0;
    }
    writeTransform(g, off, sc, rot);
  }

  /* ---------- Panel ---------- */
  function renderPanel() {
    panel.innerHTML = '';
    if (!selected) return;

    if (typeof opt.renderInfo === 'function') {
      const custom = opt.renderInfo(selected.raw, selected);
      if (custom == null) return;
      if (typeof custom === 'string') panel.innerHTML = custom;
      else panel.appendChild(custom);
      return;
    }

    const info = selected.info || {};
    const box = document.createElement('div');
    box.className = 'sb-info';
    let html = '';
    if (info.eyebrow) html += `<p class="sb-info__eyebrow">${escapeHtml(info.eyebrow)}</p>`;
    {
      const titleText = escapeHtml(info.title || selected.label);
      const titleHref = info.href || selected.href || null;
      html += titleHref
        ? `<h3 class="sb-info__title"><a href="${escapeHtml(titleHref)}">${titleText}</a></h3>`
        : `<h3 class="sb-info__title">${titleText}</h3>`;
    }
    html += `<span class="sb-info__rule"></span>`;
    if (info.text) html += `<p class="sb-info__text">${escapeHtml(info.text)}</p>`;
    if (Array.isArray(info.items) && info.items.length) {
      if (info.itemsLabel) html += `<p class="sb-info__label">${escapeHtml(info.itemsLabel)}</p>`;
      html += '<ul class="sb-info__list">' + info.items.map(it => {
        const label = escapeHtml(typeof it === 'string' ? it : it.label);
        return `<li>${(typeof it === 'object' && it.href)
          ? `<a href="${escapeHtml(it.href)}">${label}</a>`
          : `<span>${label}</span>`}</li>`;
      }).join('') + '</ul>';
    }
    box.innerHTML = html;
    panel.appendChild(box);
  }

  /* ---------- Aktionen ---------- */
  /* Einziger Weg, den Zustand zu ändern: berechnet die Richtung des Wechsels
     und gibt sie an render() weiter. */
  function commit(nextPath, nextSelected) {
    const mode = pathMode(focusPath, nextPath);
    focusPath = nextPath;
    selected = nextSelected;
    syncHash();
    render(mode);
  }

  function activate(node) {
    // Vorfahrenkette [root, …, node] – unabhängig davon, in welchem Ring
    // der Knoten gerade gezeichnet ist.
    const chain = [];
    for (let n = node; n; n = n.parent) chain.unshift(n);

    overviewAll = false;

    if (node.children.length && chain.length <= ringCount) {
      const isOpen = focusPath[focusPath.length - 1] === node;
      pendingFocusId = null;
      commit((isOpen && opt.collapseOnRepeat) ? chain.slice(0, -1) : chain, null);
      emit('open', { node: node.raw, path: pathIds() });
      announce(node.label);
      return;
    }

    // Blatt (oder maximale Ringtiefe erreicht): die Ebene des Blattes wird
    // nach außen geholt, damit die ausgeworfene Karte immer in Ring 0 liegt.
    const wanted = chain.slice(0, -1);
    const same = wanted.length === focusPath.length && wanted.every((n, i) => n === focusPath[i]);
    pendingFocusId = node.id;
    commit(wanted, (same && selected === node) ? null : node);
    emit('select', { node: selected ? selected.raw : null, path: pathIds() });
    if (selected) announce(selected.label);
  }

  /* Springt auf die Ebene, die vor focusPath[index] lag. */
  function goTo(index) {
    overviewAll = false;
    commit(focusPath.slice(0, index), null);
    emit('back', { path: pathIds() });
    announce(focusPath[focusPath.length - 1].label || opt.i18n.root);
  }

  function back() {
    if (selected) { commit(focusPath, null); emit('back', { path: pathIds() }); return; }
    if (overviewAll) { resetToRoot(); return; }
    if (focusPath.length > 1) return goTo(focusPath.length - 1);
  }

  function resetToRoot() {
    if (focusPath.length === 1 && !selected && !overviewAll) return;
    overviewAll = false;
    pendingFocusId = null;
    hoverId = null;
    commit([tree], null);
    emit('back', { path: pathIds() });
    announce(opt.data.label || opt.i18n.root);
  }

  /* Root-Kern: alle Level-2 öffnen. Sonst wie Zurück/Reset. */
  function onCoreActivate() {
    if (focusPath.length > 1 || selected || overviewAll) {
      resetToRoot();
      return;
    }
    openOverviewAll();
  }

  function openOverviewAll() {
    const hasLevel2 = tree.children.some(c => c.children.length);
    if (!hasLevel2) return;
    overviewAll = true;
    selected = null;
    pendingFocusId = null;
    hoverId = null;
    focusPath = [tree];
    syncHash();
    render('deeper');
    emit('open', { node: null, path: pathIds(), overviewAll: true });
    announce(opt.i18n.openAll || opt.data.label || opt.i18n.root);
  }

  function announce(text) { live.textContent = text; }
  function pathIds() { return focusPath.slice(1).map(n => n.id).concat(selected ? [selected.id] : []); }

  /* ---------- Tastatur ---------- */
  /* Pfeiltasten laufen entlang der GERENDERTEN Nachbarn, nicht entlang der
     Baumgeschwister – im äußeren Ring stehen jetzt Knoten aus mehreren Ebenen. */
  function onSegmentKey(e, run) {
    const g = e.currentTarget;
    const ring = g.getAttribute('data-level');
    const sibs = [...gLevels.querySelectorAll(`.sb-seg:not(.is-exiting)[data-level="${ring}"]`)];
    const i = sibs.indexOf(g);
    let target = null;
    switch (e.key) {
      case 'ArrowRight': case 'ArrowDown': target = sibs[(i + 1) % sibs.length]; break;
      case 'ArrowLeft':  case 'ArrowUp':   target = sibs[(i - 1 + sibs.length) % sibs.length]; break;
      case 'Enter': case ' ': case 'Spacebar': e.preventDefault(); run(); return;
      case 'Escape': case 'Backspace': e.preventDefault(); back(); return;
      case 'Home': target = sibs[0]; break;
      case 'End': target = sibs[sibs.length - 1]; break;
      default: return;
    }
    e.preventDefault();
    if (target) target.focus();
  }

  /* ---------- Hash-Routing ---------- */
  function syncHash() {
    if (!opt.hashRouting) return;
    const ids = pathIds();
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    if (ids.length) params.set(opt.hashKey, ids.join('/')); else params.delete(opt.hashKey);
    const str = params.toString().replace(/%2F/gi, '/');
    history.replaceState(null, '', str ? `#${str}` : location.pathname + location.search);
  }

  function readHash() {
    if (!opt.hashRouting) return;
    const params = new URLSearchParams(location.hash.replace(/^#/, ''));
    const raw = params.get(opt.hashKey);
    if (!raw) return;
    openPath(raw.split('/').filter(Boolean), false);
  }

  function openPath(ids, updateHash = true) {
    overviewAll = false;
    let path = [tree];
    let leaf = null;
    for (const id of ids) {
      const n = registry.get(String(id));
      if (!n) break;
      if (n.children.length && path.length < ringCount) path.push(n);
      else { leaf = n; break; }
    }
    const mode = pathMode(focusPath, path);
    focusPath = path;
    selected = leaf;
    if (updateHash) syncHash();
    render(mode);
  }

  /* ---------- Compact-Modus (Viewport, nicht Container) ---------- */
  const compactMq = window.matchMedia(`(max-width: ${opt.compactBreakpoint}px)`)
  const syncCompact = () => root.classList.toggle('is-compact', compactMq.matches)
  syncCompact()
  compactMq.addEventListener('change', syncCompact)

  // Label-Tokens ändern sich per CSS @media — Labels neu einlesen
  const labelMqPad = window.matchMedia('(max-width: 1490px)')
  const labelMqPhone = window.matchMedia('(max-width: 620px)')
  const syncLabelTokens = () => applyRingGeometry(true)
  labelMqPad.addEventListener('change', syncLabelTokens)
  labelMqPhone.addEventListener('change', syncLabelTokens)

  let ro = null;
  if ('ResizeObserver' in window) {
    const syncRingScale = () => applyRingGeometry(false);

    ro = new ResizeObserver(() => syncRingScale());
    ro.observe(root);
    // Einmal nach Layout: Ringe an tatsächliche SVG-Breite anpassen
    requestAnimationFrame(() => applyRingGeometry(true));
  }

  backBtn.addEventListener('click', back);
  const onHash = () => readHash();
  if (opt.hashRouting) window.addEventListener('hashchange', onHash);

  /* ---------- Init ---------- */
  paintDecor();
  paintCore();
  render();
  readHash();

  /* ---------- Public API ---------- */
  const api = {
    open: ids => {
      openPath(Array.isArray(ids) ? ids : [ids]);
      emit('open', {
        node: focusPath.length > 1 ? focusPath[focusPath.length - 1].raw : null,
        path: pathIds()
      });
      return api;
    },
    select: id => { const n = registry.get(String(id)); if (n) activate(n); return api; },
    back: () => { back(); return api; },
    reset: () => { resetToRoot(); return api; },
    getState: () => ({
      path: pathIds(),
      selected: selected ? selected.raw : null,
      overviewAll
    }),
    on, off,
    destroy() {
      timers.forEach(clearTimeout); timers.clear();
      liveSegs.forEach(g => { if (g._tween) cancelAnimationFrame(g._tween); });
      liveSegs.clear();
      if (ro) ro.disconnect();
      compactMq.removeEventListener('change', syncCompact);
      labelMqPad.removeEventListener('change', syncLabelTokens);
      labelMqPhone.removeEventListener('change', syncLabelTokens);
      if (opt.hashRouting) window.removeEventListener('hashchange', onHash);
      root.innerHTML = '';
      root.classList.remove('sb', 'is-compact', 'is-drilled', 'is-overview-all');
      for (const k in listeners) delete listeners[k];
    },
    element: root
  };
  return api;
}

if (typeof window !== 'undefined') window.SunburstNav = { create: createSunburstNav };

function parseJsonAttr(raw) {
	if (!raw || !String(raw).trim()) return null;
	try {
		return JSON.parse(raw);
	} catch (err) {
		console.warn('[SunburstNav] data-Attribut ist kein gültiges JSON.', err);
		return null;
	}
}

/** Page-Init: mountet das Leistungsrad auf `[data-sunburst]`. CMS-JSON bevorzugt, Demo als Fallback. */
export function sunburst() {
	const hosts = document.querySelectorAll('[data-sunburst]')
	if (!hosts.length) return

	hosts.forEach((host) => {
		const fromCms = parseJsonAttr(host.getAttribute('data-sunburst-data'))
		const hasChildren = fromCms && Array.isArray(fromCms.children) && fromCms.children.length > 0
		const data = hasChildren ? fromCms : DEMO_SUNBURST_DATA
		if (!hasChildren && fromCms) {
			console.warn('[SunburstNav] CMS-Baum ohne Kinder – Fallback auf Demo-Daten.')
		}

		const i18n = parseJsonAttr(host.getAttribute('data-sunburst-i18n'))
		const centerFromCms = parseJsonAttr(host.getAttribute('data-sunburst-center'))
		const center = {
			eyebrow: (centerFromCms && centerFromCms.eyebrow) || '',
			title: (centerFromCms && centerFromCms.title) || '360°',
			titleBase: 0.08
		}

		const options = {
			data,
			center,
			maxRings: 2,
			decor: true,
			ringWidths: [186, 120],
			coreRadius: 0.28,
			coreRadiusRoot: 0.28,
			coreGap: 51,
			padAngle: 0.9,
			spanOuterPad: 28,
			backLabel: 'none',
			detach: { offset: 27 },
			labels: {
				maxSizePx: 21,
				branchPad: 32,
				branchPadAngle: 3,
				branchMaxChars: 18,
				branchMaxLines: 4
			},
			compactBreakpoint: 1280,
			hashRouting: false
		}
		if (i18n) options.i18n = i18n

		const nav = createSunburstNav(host, options)

		// Infobox-Seite: Segment in der rechten Radhälfte → Panel rechts
		const placePanel = () => {
			const sel = nav.getState().selected
			const escapeId = (window.CSS && CSS.escape) ? CSS.escape : (s) => String(s).replace(/["\\]/g, '\\$&')
			const seg = sel && host.querySelector(
				`.sb-seg:not(.is-exiting)[data-id="${escapeId(sel.id)}"]`
			)
			if (!seg) {
				host.classList.remove('is-panel-right')
				return
			}
			const svg = host.querySelector('.sb__svg')
			if (!svg) return
			const wheel = svg.getBoundingClientRect()
			const box = seg.getBoundingClientRect()
			host.classList.toggle(
				'is-panel-right',
				(box.left + box.right) / 2 > wheel.left + wheel.width / 2
			)
		}

		// Compact: Infobox liegt unter dem Rad — bei Auswahl dorthin scrollen,
		// sonst bleibt sie unterhalb des Viewports unsichtbar.
		const scrollPanelIntoView = () => {
			if (!host.classList.contains('is-compact')) return
			if (!nav.getState().selected) return

			const panelEl = host.querySelector('.sb__panel')
			if (!panelEl || !panelEl.children.length) return

			const rect = panelEl.getBoundingClientRect()
			const headerH = parseFloat(
				getComputedStyle(document.documentElement).getPropertyValue('--header-height')
			) || 90
			const gap = parseFloat(
				getComputedStyle(document.documentElement).getPropertyValue('--gap')
			) || 16
			const topLimit = headerH + gap
			// Schon gut sichtbar (oberes Drittel unter Header) → kein Jump
			if (rect.top >= topLimit && rect.top < window.innerHeight * 0.55) return

			const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
			panelEl.scrollIntoView({
				behavior: reduced ? 'auto' : 'smooth',
				block: 'start',
				inline: 'nearest'
			})
		}

		const schedulePlacePanel = () => requestAnimationFrame(placePanel)
		nav.on('select', () => {
			requestAnimationFrame(() => {
				placePanel()
				scrollPanelIntoView()
			})
		})
		nav.on('open', schedulePlacePanel)
		nav.on('back', schedulePlacePanel)
		placePanel()

		// Kategorien oberhalb des Rads: aktiv hervorheben + Klick steuert das Rad
		const section = host.closest('#servicewheel, .section--servicewheel') || document
		const branches = [...section.querySelectorAll('[data-sunburst-branch]')]
		if (branches.length) {
			const syncBranches = () => {
				const state = nav.getState()
				if (state.overviewAll) {
					branches.forEach((el) => {
						el.classList.add('is-active')
						el.setAttribute('aria-pressed', 'true')
					})
					return
				}
				const activeId = state.path[0] || null
				branches.forEach((el) => {
					const on = !!(activeId && el.getAttribute('data-sunburst-branch') === String(activeId))
					el.classList.toggle('is-active', on)
					el.setAttribute('aria-pressed', on ? 'true' : 'false')
				})
			}

			branches.forEach((el) => {
				el.addEventListener('click', () => {
					const id = el.getAttribute('data-sunburst-branch')
					if (!id) return
					const state = nav.getState()
					// erneuter Klick auf aktive Kategorie (oder Overview) → zurück
					if (!state.overviewAll && state.path[0] === id) nav.reset()
					else nav.open(id)
				})
			})

			nav.on('open', syncBranches)
			nav.on('back', syncBranches)
			nav.on('select', syncBranches)
			syncBranches()
		}
	})
}
